/**
 * Domain vocabulary. These mirror the Postgres enums in db/migrations/001 —
 * if you change one, change both. §4 of REQUIREMENTS.md is binding on the
 * meaning of every name here.
 */

export const TICKET_STATES = [
  'new',
  'open',
  'pending_requester',
  'pending_external',
  'pending_internal',
  /** Waiting for a dated real-world event to happen; returns by itself. */
  'pending_event',
  'resolved',
  'closed',
  'cancelled',
] as const;
export type TicketState = (typeof TICKET_STATES)[number];

/**
 * Priority, lowest first. Set by a person, never derived.
 *
 * Ordered so a comparison is just an index: `PRIORITIES.indexOf(a) >
 * PRIORITIES.indexOf(b)` is "a is more urgent", and nothing has to hold a
 * second table of ranks that can disagree with this one.
 *
 * `low` is the default and means ordinary, not unimportant. Make `medium`
 * the default and you have only moved the middle — there is then nothing
 * left to say "this one genuinely is below the line".
 */
export const TICKET_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;

export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

/** Above this, the ticket is pinned to the top of every list. */
export function isPinnedPriority(p: TicketPriority): boolean {
  return p === 'critical';
}

/**
 * States whose SLA resolution clock is paused (FR-SLA-03).
 *
 * `pending_event` belongs here for the same reason as the other three:
 * waiting for somebody to land is not desk time, and charging it to the
 * resolution target would make the target measure the calendar rather than
 * the desk.
 */
export const PENDING_STATES = [
  'pending_requester',
  'pending_external',
  'pending_internal',
  'pending_event',
] as const satisfies readonly TicketState[];
export type PendingState = (typeof PENDING_STATES)[number];

/** States where no clock runs at all. */
export const TERMINAL_STATES = ['closed', 'cancelled'] as const satisfies readonly TicketState[];

/** States that count as "in the queue" for backlog and SLA sweeps. */
export const ACTIVE_STATES = [
  'new',
  'open',
  'pending_requester',
  'pending_external',
  'pending_internal',
  'pending_event',
] as const satisfies readonly TicketState[];

export function isPendingState(s: TicketState): s is PendingState {
  return (PENDING_STATES as readonly string[]).includes(s);
}

export function isTerminalState(s: TicketState): boolean {
  return (TERMINAL_STATES as readonly string[]).includes(s);
}

export function isActiveState(s: TicketState): boolean {
  return (ACTIVE_STATES as readonly string[]).includes(s);
}

/** True when the resolution clock should be accumulating in this state. */
export function clockRuns(s: TicketState): boolean {
  return s === 'new' || s === 'open';
}

export const CONVERSATION_KINDS = ['inbound', 'reply', 'note', 'system'] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

/**
 * Where an action came from.
 *
 * `assistant` is NOT `automation` (FR-API-08). Automation is the desk's own
 * work being done without a person — a rule closing a duplicate. The
 * assistant is demand that never reached the desk at all. Conflating them
 * makes it impossible to tell which of the two is improving, which is the
 * only question either number is asked to answer.
 *
 * `teams` is a request asked of the Teams assistant and filed through
 * `POST /intake/ask` (routes/intakeAsk.ts): the channel it arrived on, as
 * `email` is for mail. The database enum also carries `phone` (migration
 * 008), which this list does not — see the note in that migration about
 * phone tickets needing somewhere honest to go.
 */
export const ACTOR_SOURCES = [
  'portal',
  'email',
  'api',
  'automation',
  'system',
  'assistant',
  'teams',
] as const;
export type ActorSource = (typeof ACTOR_SOURCES)[number];

/**
 * FR-AD-03. Ordered least to most privileged; the index IS the rank, so
 * comparisons are `rankOf(a) >= rankOf(b)` and there is exactly one place
 * that knows the hierarchy.
 */
export const TENANT_ROLES = [
  'requester',
  'agent',
  'queue_lead',
  'tenant_admin',
  'platform_admin',
] as const;
export type TenantRole = (typeof TENANT_ROLES)[number];

export function rankOf(role: TenantRole): number {
  return TENANT_ROLES.indexOf(role);
}

export const FIELD_TYPES = [
  'text',
  'longtext',
  'number',
  'date',
  'datetime',
  'select',
  'multiselect',
  'checkbox',
  'user',
  'project_number',
  'email',
  'url',
  'file',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const FIELD_REQUIRED_AT = ['never', 'create', 'close'] as const;
export type FieldRequiredAt = (typeof FIELD_REQUIRED_AT)[number];

export const FIELD_VISIBILITY = ['requester', 'agent'] as const;
/** What intake does about a required field an email does not supply (061). */
export const FIELD_WHEN_MISSING_BY_EMAIL = ['leave', 'ask'] as const;
export type FieldVisibility = (typeof FIELD_VISIBILITY)[number];

/**
 * The fields a form should OFFER at a given phase.
 *
 * IN `shared` RATHER THAN `core` FOR THE SAME REASON AS THE TRANSITION TABLE:
 * the browser needs it. The create form and the resolve dialog are both built
 * in the client from one configuration payload, and the payload deliberately
 * carries every field — the API cannot return a different set per form
 * without knowing which form is asking.
 *
 * So the client has to know the rule, and there must be exactly one of it.
 *
 * WHAT IT FIXES: the new-request form rendered every field it was given, so a
 * field the desk marks `requiredAt: 'close'` — a resolution code — appeared on
 * the form for RAISING a ticket, asking how something was fixed before it had
 * been reported. Requesters never saw it, because the API strips
 * agent-visibility fields for them; staff did, which made it look like a
 * per-group configuration mistake rather than a form showing the wrong set.
 *
 * `never` means optional, not hidden, so only `close` is withheld at create.
 * Structural on purpose: the server's `FieldDefinition` and the client's
 * `FieldDef` are different types describing the same thing.
 */
export function fieldsForPhase<T extends { requiredAt: FieldRequiredAt }>(
  defs: readonly T[],
  phase: 'create' | 'close',
): T[] {
  if (phase === 'close') return [...defs];
  return defs.filter((d) => d.requiredAt !== 'close');
}

export const SCAN_STATUSES = [
  'scan_pending',
  'scan_clean',
  'scan_infected',
  'scan_skipped',
] as const;
export type ScanStatus = (typeof SCAN_STATUSES)[number];

/** FR-ID-01: every person reference in the system is this tuple. */
export interface IdentityTuple {
  /** Entra object id — the join key. */
  oid: string;
  /** Display and routing key. */
  email: string;
  /**
   * Resolved through Graph MCP. NULL when unresolved — NEVER inferred from the
   * email address (FR-ID-03).
   */
  name: string | null;
  office?: string | null;
  department?: string | null;
}

/** An address we could not resolve to an ESA identity (FR-EM-09). */
export interface ExternalContactRef {
  kind: 'external';
  email: string;
  name: string | null;
  organisation?: string | null;
}

export type PersonRef = ({ kind: 'user' } & IdentityTuple) | ExternalContactRef;

/**
 * `action` is not a notification.
 *
 * The other three tell somebody that something happened. An `action` row is
 * an instruction to change a system outside this one, and the worker treats
 * it accordingly: it is never swallowed by the notifications kill switch,
 * because an authorized action that silently did not run is worse than one
 * that failed loudly.
 */
export const OUTBOX_KINDS = ['email', 'teams', 'webhook', 'action', 'push'] as const;
export type OutboxKind = (typeof OUTBOX_KINDS)[number];

/** FR-N8N-01: the event vocabulary the automation platform consumes. */
export const EVENT_TYPES = [
  'ticket.created',
  'ticket.state_changed',
  'ticket.assigned',
  'ticket.field_changed',
  'ticket.conversation_added',
  'ticket.resolved',
  'ticket.transferred',
  'sla.at_risk',
  'sla.breached',
  // FR-SEC-03. Not a ticket event: it is about a set of them, so it carries
  // a campaign id and a ticket list rather than one ticket.
  'security.campaign_detected',
  // A person authorized a real change in another system. Carried as an event
  // because the automation server is the thing that performs it, and because
  // "who let this happen" is a question worth being able to subscribe to.
  'action.authorized',
  'action.completed',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];
